import React, { useState, useCallback, useEffect } from 'react';
import { AnimatedModal } from './AnimatedComponents';
import { useData } from '../contexts/DataContext';
import { useAuth } from '../contexts/AuthContext';
import { Sparkles, Loader2, AlertCircle, CheckCircle, Edit2, Trash2, ChevronLeft } from 'lucide-react';
import Button from './ui/Button';

interface ExtractedContent {
  title: string;
  hook: string;
  body: string;
  cta: string;
  inspiration_link?: string;
  notes?: string;
}

interface AIImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  preselectedClientId?: string;
  onSuccess?: () => void;
}

type Step = 'input' | 'preview' | 'importing' | 'success';

const AIImportModal: React.FC<AIImportModalProps> = ({
  isOpen,
  onClose,
  preselectedClientId,
  onSuccess
}) => {
  const { state, loadContenuti } = useData();
  const { getToken } = useAuth();

  const [step, setStep] = useState<Step>('input');
  const [selectedClientId, setSelectedClientId] = useState(preselectedClientId || '');
  const [rawText, setRawText] = useState('');
  const [extractedContents, setExtractedContents] = useState<ExtractedContent[]>([]);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [importResult, setImportResult] = useState<{
    created: number;
    failed: number;
  } | null>(null);

  const API_BASE_URL = '/api';

  const activeClients = state.clienti
    .filter(c => c.status === 'active')
    .sort((a, b) => a.nomeAzienda.localeCompare(b.nomeAzienda, 'it', { sensitivity: 'base' }));

  const handleAnalyze = useCallback(async () => {
    if (!rawText.trim()) {
      setError('Inserisci del testo da analizzare');
      return;
    }

    if (rawText.trim().length < 10) {
      setError('Il testo deve essere di almeno 10 caratteri');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const token = getToken();
      if (!token) {
        setError('Sessione scaduta. Effettua nuovamente il login.');
        return;
      }

      const response = await fetch(`${API_BASE_URL}/ai/parse-contents`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          raw_text: rawText,
          client_id: selectedClientId || undefined
        })
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Errore nell\'analisi');
      }

      if (!data.data.contents || data.data.contents.length === 0) {
        setError('Nessun contenuto trovato nel testo. Prova con un formato diverso o aggiungi piu\' dettagli.');
        return;
      }

      setExtractedContents(data.data.contents);
      setStep('preview');

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore sconosciuto');
    } finally {
      setIsLoading(false);
    }
  }, [rawText, selectedClientId, getToken, API_BASE_URL]);

  const handleImport = useCallback(async () => {
    if (!selectedClientId) {
      setError('Seleziona un cliente prima di importare');
      return;
    }

    if (extractedContents.length === 0) {
      setError('Nessun contenuto da importare');
      return;
    }

    setStep('importing');
    setError(null);

    try {
      const token = getToken();
      if (!token) {
        setError('Sessione scaduta. Effettua nuovamente il login.');
        setStep('preview');
        return;
      }

      const response = await fetch(`${API_BASE_URL}/ai/import-contents`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          client_id: selectedClientId,
          contents: extractedContents
        })
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Errore nell\'importazione');
      }

      setImportResult({
        created: data.data.summary.created,
        failed: data.data.summary.failed
      });
      setStep('success');

      // Refresh contenuti nella dashboard
      await loadContenuti();
      onSuccess?.();

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore sconosciuto');
      setStep('preview');
    }
  }, [selectedClientId, extractedContents, getToken, API_BASE_URL, loadContenuti, onSuccess]);

  const handleEditContent = (index: number, field: keyof ExtractedContent, value: string) => {
    setExtractedContents(prev => prev.map((c, i) =>
      i === index ? { ...c, [field]: value } : c
    ));
  };

  const handleRemoveContent = (index: number) => {
    setExtractedContents(prev => prev.filter((_, i) => i !== index));
    if (editingIndex === index) {
      setEditingIndex(null);
    }
  };

  const handleClose = () => {
    setStep('input');
    setRawText('');
    setExtractedContents([]);
    setError(null);
    setImportResult(null);
    setEditingIndex(null);
    setSelectedClientId(preselectedClientId || '');
    onClose();
  };

  const handleBack = () => {
    setStep('input');
    setError(null);
  };

  // Effetto per aggiornare selectedClientId quando cambia preselectedClientId
  useEffect(() => {
    if (preselectedClientId && isOpen) {
      setSelectedClientId(preselectedClientId);
    }
  }, [preselectedClientId, isOpen]);

  // Footer content based on step
  const renderFooter = () => {
    if (step === 'input') {
      return (
        <>
          <Button
            variant="secondary"
            onClick={handleClose}
            className="min-h-[48px] sm:min-h-[40px] flex-1 sm:flex-none"
          >
            Annulla
          </Button>
          <Button
            variant="primary"
            onClick={handleAnalyze}
            disabled={isLoading || !rawText.trim()}
            isLoading={isLoading}
            icon={Sparkles}
            className="min-h-[48px] sm:min-h-[40px] flex-1 sm:flex-none"
          >
            {isLoading ? 'Analizzando...' : 'Analizza con AI'}
          </Button>
        </>
      );
    }

    if (step === 'preview') {
      return (
        <>
          <Button
            variant="secondary"
            onClick={handleBack}
            className="min-h-[48px] sm:min-h-[40px] flex-1 sm:flex-none"
          >
            Modifica testo
          </Button>
          <Button
            variant="primary"
            onClick={handleImport}
            disabled={!selectedClientId || extractedContents.length === 0}
            className="min-h-[48px] sm:min-h-[40px] flex-1 sm:flex-none"
          >
            Importa {extractedContents.length} contenut{extractedContents.length === 1 ? 'o' : 'i'}
          </Button>
        </>
      );
    }

    if (step === 'success') {
      return (
        <Button
          variant="primary"
          onClick={handleClose}
          className="min-h-[48px] sm:min-h-[40px] w-full sm:w-auto"
        >
          Chiudi
        </Button>
      );
    }

    return null;
  };

  return (
    <AnimatedModal
      isOpen={isOpen}
      onClose={handleClose}
      title="Import Contenuti con AI"
      size="xl"
      footer={renderFooter()}
    >
      {/* Step 1: Input */}
      {step === 'input' && (
        <div className="space-y-4">
          <div className="flex items-start sm:items-center gap-2 p-3 bg-purple-50 dark:bg-purple-900/20 rounded-xl text-purple-700 dark:text-purple-300 text-sm">
            <Sparkles className="w-5 h-5 flex-shrink-0 mt-0.5 sm:mt-0" />
            <span>
              Incolla il testo con i contenuti da importare. L'AI analizzerà e estrarrà automaticamente hook, body e CTA per ogni video.
            </span>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              Cliente (opzionale per l'analisi)
            </label>
            <select
              value={selectedClientId}
              onChange={(e) => setSelectedClientId(e.target.value)}
              className="w-full px-4 py-3 text-base sm:text-sm border border-slate-200 dark:border-gray-600 rounded-xl focus:outline-none focus:border-[#fdf751] focus:ring-2 focus:ring-[#fdf751]/20 bg-white dark:bg-gray-700 text-slate-900 dark:text-slate-100"
            >
              <option value="">Seleziona cliente...</option>
              {activeClients.map(client => (
                <option key={client.id} value={client.id}>
                  {client.nomeAzienda}
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
              Selezionare un cliente aiuta l'AI a contestualizzare meglio i contenuti
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              Testo da analizzare
            </label>
            <textarea
              value={rawText}
              onChange={(e) => setRawText(e.target.value)}
              placeholder={`Incolla qui il testo con i contenuti da importare...

Esempio:
Video 1: Come aumentare le vendite
Hook: Vuoi triplicare le tue vendite in 30 giorni?
Corpo: In questo video ti mostro 3 strategie pratiche...
CTA: Seguimi per altri consigli di business!`}
              className="w-full h-48 sm:h-64 px-4 py-3 text-base sm:text-sm border border-slate-200 dark:border-gray-600 rounded-xl focus:outline-none focus:border-[#fdf751] focus:ring-2 focus:ring-[#fdf751]/20 resize-none bg-white dark:bg-gray-700 text-slate-900 dark:text-slate-100 placeholder-slate-400"
            />
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
              {rawText.length}/50000 caratteri
            </p>
          </div>

          {error && (
            <div className="flex items-start sm:items-center gap-2 p-3 bg-red-50 dark:bg-red-900/20 rounded-xl text-red-700 dark:text-red-300 text-sm">
              <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5 sm:mt-0" />
              <span>{error}</span>
            </div>
          )}
        </div>
      )}

      {/* Step 2: Preview */}
      {step === 'preview' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <button
                onClick={handleBack}
                className="p-2 sm:p-1 hover:bg-slate-100 dark:hover:bg-gray-700 rounded-lg transition-colors active:scale-95"
              >
                <ChevronLeft className="w-5 h-5 text-slate-500" />
              </button>
              <h3 className="font-medium text-slate-900 dark:text-slate-100 text-sm sm:text-base">
                {extractedContents.length} contenut{extractedContents.length === 1 ? 'o trovato' : 'i trovati'}
              </h3>
            </div>
          </div>

          {!selectedClientId && (
            <div className="p-3 bg-amber-50 dark:bg-amber-900/20 rounded-xl text-amber-700 dark:text-amber-300 text-sm">
              <strong>Attenzione:</strong> Seleziona un cliente prima di importare.
              <select
                value={selectedClientId}
                onChange={(e) => setSelectedClientId(e.target.value)}
                className="mt-2 w-full px-3 py-3 sm:py-2 text-base sm:text-sm border border-amber-200 dark:border-amber-700 rounded-lg focus:outline-none focus:border-amber-400 bg-white dark:bg-gray-700 text-slate-900 dark:text-slate-100"
              >
                <option value="">Seleziona cliente...</option>
                {activeClients.map(client => (
                  <option key={client.id} value={client.id}>
                    {client.nomeAzienda}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="space-y-3 max-h-[50vh] sm:max-h-96 overflow-y-auto pr-1 -mr-1">
            {extractedContents.map((content, index) => (
              <div
                key={index}
                className="p-4 border border-slate-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-800"
              >
                <div className="flex items-start justify-between mb-2">
                  <span className="px-2 py-1 bg-slate-100 dark:bg-gray-700 rounded text-xs font-medium text-slate-700 dark:text-slate-300">
                    Video {index + 1}
                  </span>
                  <div className="flex gap-1">
                    <button
                      onClick={() => setEditingIndex(editingIndex === index ? null : index)}
                      className={`p-2 sm:p-1.5 rounded transition-colors active:scale-95 ${
                        editingIndex === index
                          ? 'text-purple-600 bg-purple-50 dark:bg-purple-900/30'
                          : 'text-slate-400 hover:text-slate-600 hover:bg-slate-100 dark:hover:bg-gray-700'
                      }`}
                      title={editingIndex === index ? 'Chiudi modifica' : 'Modifica'}
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleRemoveContent(index)}
                      className="p-2 sm:p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded transition-colors active:scale-95"
                      title="Rimuovi"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {editingIndex === index ? (
                  <div className="space-y-3">
                    <div>
                      <label className="text-xs font-medium text-slate-500 dark:text-slate-400">Titolo</label>
                      <input
                        type="text"
                        value={content.title}
                        onChange={(e) => handleEditContent(index, 'title', e.target.value)}
                        placeholder="Titolo"
                        className="w-full px-3 py-3 sm:py-2 border border-slate-200 dark:border-gray-600 rounded-lg text-base sm:text-sm bg-white dark:bg-gray-700 text-slate-900 dark:text-slate-100"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-slate-500 dark:text-slate-400">Hook</label>
                      <textarea
                        value={content.hook}
                        onChange={(e) => handleEditContent(index, 'hook', e.target.value)}
                        placeholder="Hook"
                        className="w-full px-3 py-3 sm:py-2 border border-slate-200 dark:border-gray-600 rounded-lg text-base sm:text-sm resize-none bg-white dark:bg-gray-700 text-slate-900 dark:text-slate-100"
                        rows={2}
                      />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-slate-500 dark:text-slate-400">Body</label>
                      <textarea
                        value={content.body}
                        onChange={(e) => handleEditContent(index, 'body', e.target.value)}
                        placeholder="Body"
                        className="w-full px-3 py-3 sm:py-2 border border-slate-200 dark:border-gray-600 rounded-lg text-base sm:text-sm resize-none bg-white dark:bg-gray-700 text-slate-900 dark:text-slate-100"
                        rows={3}
                      />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-slate-500 dark:text-slate-400">CTA</label>
                      <input
                        type="text"
                        value={content.cta}
                        onChange={(e) => handleEditContent(index, 'cta', e.target.value)}
                        placeholder="CTA"
                        className="w-full px-3 py-3 sm:py-2 border border-slate-200 dark:border-gray-600 rounded-lg text-base sm:text-sm bg-white dark:bg-gray-700 text-slate-900 dark:text-slate-100"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-slate-500 dark:text-slate-400">Link Ispirazione</label>
                      <input
                        type="url"
                        value={content.inspiration_link || ''}
                        onChange={(e) => handleEditContent(index, 'inspiration_link', e.target.value)}
                        placeholder="https://..."
                        className="w-full px-3 py-3 sm:py-2 border border-slate-200 dark:border-gray-600 rounded-lg text-base sm:text-sm bg-white dark:bg-gray-700 text-slate-900 dark:text-slate-100"
                      />
                    </div>
                  </div>
                ) : (
                  <div className="space-y-1.5 text-sm">
                    <p className="font-medium text-slate-900 dark:text-slate-100">{content.title}</p>
                    {content.inspiration_link && (
                      <p className="text-slate-600 dark:text-slate-400">
                        <span className="font-medium text-slate-700 dark:text-slate-300">Link:</span>{' '}
                        <a
                          href={content.inspiration_link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-purple-600 dark:text-purple-400 hover:underline break-all"
                        >
                          {content.inspiration_link}
                        </a>
                      </p>
                    )}
                    {content.hook && (
                      <p className="text-slate-600 dark:text-slate-400">
                        <span className="font-medium text-slate-700 dark:text-slate-300">Hook:</span> {content.hook}
                      </p>
                    )}
                    {content.body && (
                      <p className="text-slate-600 dark:text-slate-400 line-clamp-2">
                        <span className="font-medium text-slate-700 dark:text-slate-300">Body:</span> {content.body}
                      </p>
                    )}
                    {content.cta && (
                      <p className="text-slate-600 dark:text-slate-400">
                        <span className="font-medium text-slate-700 dark:text-slate-300">CTA:</span> {content.cta}
                      </p>
                    )}
                    {content.notes && (
                      <p className="text-slate-500 dark:text-slate-500 italic text-xs">
                        {content.notes}
                      </p>
                    )}
                    {!content.hook && !content.body && !content.cta && content.inspiration_link && (
                      <p className="text-amber-600 dark:text-amber-400 text-xs mt-1">
                        Solo riferimento video - contenuto da definire
                      </p>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>

          {extractedContents.length === 0 && (
            <div className="text-center py-8 text-slate-500 dark:text-slate-400">
              Tutti i contenuti sono stati rimossi. Torna indietro per analizzare nuovo testo.
            </div>
          )}

          {error && (
            <div className="flex items-start sm:items-center gap-2 p-3 bg-red-50 dark:bg-red-900/20 rounded-xl text-red-700 dark:text-red-300 text-sm">
              <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5 sm:mt-0" />
              <span>{error}</span>
            </div>
          )}
        </div>
      )}

      {/* Step 3: Importing */}
      {step === 'importing' && (
        <div className="flex flex-col items-center justify-center py-12 sm:py-16">
          <Loader2 className="w-12 h-12 text-purple-500 animate-spin mb-4" />
          <p className="text-slate-600 dark:text-slate-400">Importazione in corso...</p>
          <p className="text-sm text-slate-500 dark:text-slate-500 mt-1">
            Creazione di {extractedContents.length} contenut{extractedContents.length === 1 ? 'o' : 'i'}
          </p>
        </div>
      )}

      {/* Step 4: Success */}
      {step === 'success' && importResult && (
        <div className="flex flex-col items-center justify-center py-12 sm:py-16">
          <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mb-4">
            <CheckCircle className="w-8 h-8 text-green-600 dark:text-green-400" />
          </div>
          <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-2 text-center">
            Importazione completata!
          </h3>
          <p className="text-slate-600 dark:text-slate-400 text-center mb-6 px-4">
            {importResult.created} contenut{importResult.created === 1 ? 'o creato' : 'i creati'} con successo
            {importResult.failed > 0 && (
              <span className="text-amber-600 dark:text-amber-400">
                , {importResult.failed} non importat{importResult.failed === 1 ? 'o' : 'i'}
              </span>
            )}
          </p>
        </div>
      )}
    </AnimatedModal>
  );
};

export default AIImportModal;
